import { alpha, createTheme } from '@mui/material/styles';

const primaryFont = '"TT Norms Pro Regular", Arial, sans-serif';
const headingFont = '"TT Travels Medium", "TT Norms Pro Regular", Arial, sans-serif';

let theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1826c8',
      light: '#3d4bd4',
      dark: '#101a9c',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff5757',
      light: '#ff8585',
      dark: '#d83b3b',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f2f7fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#626262',
    },
    divider: alpha('#000000', 0.12),
  },
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily: primaryFont,
    h1: { fontFamily: headingFont, fontWeight: 600, letterSpacing: '-0.02em' },
    h2: { fontFamily: headingFont, fontWeight: 600, letterSpacing: '-0.02em' },
    h3: { fontFamily: headingFont, fontWeight: 600, letterSpacing: '-0.02em' },
    h4: { fontFamily: headingFont, fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontFamily: headingFont, fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontFamily: headingFont, fontWeight: 600, letterSpacing: '-0.01em' },
    button: { fontWeight: 600 },
  },
});

theme = createTheme(theme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--app-gradient': 'linear-gradient(180deg, #f7f7f7 0%, #eeeeee 100%)',
          '--app-glass': 'rgba(255, 255, 255, 0.78)',
        },
        'html, body, #root': {
          minHeight: '100%',
        },
        body: {
          margin: 0,
          fontFamily: primaryFont,
          background: 'var(--app-gradient)',
          color: theme.palette.text.primary,
          overflowX: 'hidden',
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
        },
        '*': {
          boxSizing: 'border-box',
        },
        '::selection': {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
        },
        '.MuiTableContainer-root': {
          borderRadius: 14,
          overflow: 'hidden',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: `1px solid ${alpha(theme.palette.primary.dark, 0.08)}`,
          backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.92)} 0%, ${alpha(theme.palette.common.white, 0.86)} 100%)`,
          boxShadow: `0 10px 26px ${alpha(theme.palette.common.black, 0.06)}`,
          transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: primaryFont,
          textTransform: 'none',
          borderRadius: 12,
          transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedPrimary: {
          backgroundImage: `linear-gradient(130deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.25)}`,
          '&:hover': {
            boxShadow: `0 14px 26px ${alpha(theme.palette.primary.main, 0.33)}`,
          },
          '&:active': {
            backgroundImage: `linear-gradient(130deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 500,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 4,
          borderRadius: 999,
          backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
          borderRadius: 10,
          transition: 'background-color 180ms ease, color 180ms ease',
          '&.Mui-selected': {
            color: theme.palette.text.primary,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'box-shadow 160ms ease',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.primary.main, 0.5),
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.14)}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 160ms ease',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          overflow: 'hidden',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 10,
          fontSize: '0.78rem',
          backdropFilter: 'blur(5px)',
          backgroundColor: alpha('#0f1f39', 0.9),
        },
      },
    },
  },
});

export default theme;
